import { RefObject, useEffect } from "react";

import { AnchorOffsets, anchorTooltipToTrigger } from "./anchorTooltipToTrigger";

/**
 * Keeps a body-portalled tooltip anchored to its trigger while it is open.
 *
 * Anchoring once on open is not enough: a `fixed` box does not move with its trigger, so a
 * window resize or a scroll of the grid underneath leaves it pointing at nothing. The `scroll`
 * listener is registered with capture so it also hears scrolls of inner containers. Both
 * listeners are removed as soon as the tooltip closes or the component unmounts (PREC-4675).
 */
export const useAnchoredTooltip = (
	triggerRef: RefObject<HTMLElement | null>,
	tooltipRef: RefObject<HTMLElement | null>,
	isVisible: boolean,
	{ offsetY, offsetX }: AnchorOffsets = {}
): void => {
	useEffect(() => {
		if (!isVisible) {
			return;
		}

		const anchor = (event?: Event) => {
			const tooltip = tooltipRef.current;

			if (!tooltip) {
				return;
			}

			// A constrained tooltip is its own scroll container. Re-anchoring on its own scroll
			// would clear `maxHeight`, measure the full height and clamp `scrollTop` back to 0,
			// so the user could never scroll down to Save (PREC-4675). Page and grid scrolls
			// still arrive with a target outside the tooltip and do re-anchor.
			if (
				event?.type === "scroll" &&
				event.target instanceof Node &&
				tooltip.contains(event.target)
			) {
				return;
			}

			anchorTooltipToTrigger(triggerRef.current, tooltip, { offsetY, offsetX });
		};

		anchor();
		window.addEventListener("resize", anchor);
		window.addEventListener("scroll", anchor, true);

		return () => {
			window.removeEventListener("resize", anchor);
			window.removeEventListener("scroll", anchor, true);
		};
	}, [isVisible, triggerRef, tooltipRef, offsetY, offsetX]);
};
